// Tactical-zone enemy AI: a stimulus + memory state machine, scoped to v4's
// turn-based tactical mode only.
//
// V4's tactical loop is explicitly built around enemies that see, hear, and
// react (vision cones, noise, pursuit, permadeath stakes), so this module owns
// the stimulus+memory machine scoped only to tactical zones.
//
// States:
//   wander   default corridor drift (least-recently-visited exits)
//   alerted  heard something; walking to where the sound was
//   pursue   saw/touched a target; chasing its LAST KNOWN position
//   search   short mill-around where the trail went cold
//   pinned   stalled at a roadblock (timed)
//
// Sight is cone-gated (vision-cone.mjs) instead of the original's plain
// omnidirectional radius — enemies carry a facingDegrees updated from
// movement heading each tick. Contact (touching range) stays omnidirectional
// always; a cone doesn't matter for something bumping into you.

import { distanceInMeters } from "../road-graph.mjs";
import {
  facingBearingDegrees,
  nearestTargetInCone,
  VISION_CONE_DEGREES,
  VISION_RANGE_METERS,
} from "./vision-cone.mjs";

export const ENEMY_STATES = Object.freeze({
  wander: "wander",
  alerted: "alerted",
  pursue: "pursue",
  search: "search",
  pinned: "pinned",
});

// Enemy archetypes (their idle behavior when nothing is sensed). A guard holds
// an intersection and rotates its facing; a patroller walks one street back and
// forth. Both still see/hear/pursue exactly like a plain enemy — the type only
// changes what they do while calm (the `wander` state). Enemies with no type
// (e.g. threat reinforcements) keep the original corridor-drift wander.
// A raider (base-defense attacker) has no idle wander: it marches on its
// assigned egress door, chips it down if it's shut (emitting attack-object),
// then floods to its interior goal once through. It still senses/pursues
// defenders like anyone else. Its assault target rides on enemy.assault =
// { doorObjectId, doorCoordinate, interiorCoordinate }.
export const ENEMY_TYPES = Object.freeze({ guard: "guard", patroller: "patroller", raider: "raider" });
// How close a raider must be to its door to swing at it, and its per-turn chip.
export const RAIDER_DOOR_REACH_METERS = 6;
export const RAIDER_DOOR_DAMAGE = 1;
// A guard re-aims to a different street of its intersection every 3–5 turns.
export const GUARD_ROTATE_MIN_TURNS = 3;
export const GUARD_ROTATE_MAX_TURNS = 5;
// Close enough to its post to count as "on station" (else it walks back).
const GUARD_ON_POST_METERS = 5;
// A guard on station watches a WIDER arc (it's actively scanning the
// intersection) but at the normal range; the moment it moves to give chase it
// narrows back to the default enemy cone. Applied by enemyVisionFor().
export const GUARD_STATIONARY_CONE_MULTIPLIER = 1.7;
export const GUARD_STATIONARY_SIGHT_MULTIPLIER = 1; // range unchanged (width only)

// The sight cone/range for one enemy, given the zone defaults. A guard that's
// still calm at its post (wander) gets the wider arc; everyone else — including
// a guard that has started chasing — uses the defaults unchanged. Shared by the
// sim (sense) and the supply-run view (cone rendering) so the drawn cone always
// matches what the enemy can actually see.
export function enemyVisionFor(enemy, coneDegrees, sightMeters) {
  if (enemy?.enemyType === ENEMY_TYPES.guard && enemy.behavior === ENEMY_STATES.wander) {
    return {
      coneDegrees: Math.min(180, coneDegrees * GUARD_STATIONARY_CONE_MULTIPLIER),
      sightMeters: sightMeters * GUARD_STATIONARY_SIGHT_MULTIPLIER,
    };
  }
  return { coneDegrees, sightMeters };
}

// Small deterministic hash so guard rotation timing/choice varies per enemy
// without an rng dependency (keeps the sim reproducible + unit-testable).
function hashInt(value) {
  let hash = 2166136261;
  const string = String(value);
  for (let i = 0; i < string.length; i += 1) {
    hash ^= string.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export const ENEMY_CONTACT_METERS = 8;
// A base defense fights at close quarters: enough reach for a defender to strike a
// raider chipping the door they're holding (defenders sit ~3.5–4.5 m back from a
// door at room-center spawns), but far short of the raid's looser 8 m so nobody
// snipes across the base. Doubles as the cursor's attack-snap radius (clicking
// within strike range targets the enemy; beyond it is a move). Walls still block
// the strike (see strikeOccluders) so this can't reach through a solid wall.
export const DEFENSE_CONTACT_METERS = 5;
export const REACTION_DELAY_MINUTES = 1;
export const NOISE_REFRESH_MINUTES = 2;
export const SEARCH_DWELL_MINUTES = 8;
// One melee round per turn: an enemy that stays adjacent re-emits contact every
// turn (each turn is 1 game minute), advancing the round-per-turn brawl so the
// player can flee or bring help between rounds.
export const CONTACT_REPEAT_MINUTES = 1;
const REPATH_INTERVAL_GAME_MINUTES = 2;

// Territorial anchors carry over unchanged from the strategic-map model:
// each enemy has a home (anchorCoordinate) it haunts. TERRITORY_RADIUS is the
// soft radius it drifts within while undisturbed; LEASH_RADIUS is how far it
// will chase before giving up and walking home.
export const TERRITORY_RADIUS_METERS = 150;
export const LEASH_RADIUS_METERS = 1000;

const STATE_PRIORITY = {
  [ENEMY_STATES.wander]: 0,
  [ENEMY_STATES.search]: 1,
  [ENEMY_STATES.alerted]: 2,
  [ENEMY_STATES.pursue]: 3,
};

export function normalizeEnemyState(behavior) {
  return Object.values(ENEMY_STATES).includes(behavior) ? behavior : ENEMY_STATES.wander;
}

export function enemyMemory(enemy) {
  return enemy.memory || {
    lastKnown: null,
    pendingState: null,
    reactAtGameMinute: null,
    noiseRefreshAtGameMinute: 0,
    searchUntilGameMinute: null,
    lastContactAtGameMinute: null,
    pathTargetCoordinate: null,
    lastRepathAtGameMinute: null,
  };
}

// --- The tick -------------------------------------------------------------

export function advanceEnemy(enemy, context) {
  const effects = [];
  let next = { ...enemy, behavior: normalizeEnemyState(enemy.behavior), memory: { ...enemyMemory(enemy) } };
  const beforeState = next.behavior;
  const beforeGoal = next.memory.lastKnown?.sourceId ?? null;
  const previousPosition = Array.isArray(next.position) ? [...next.position] : null;

  next = sense(next, context, effects);
  next = applyPendingState(next, context);
  next = act(next, context, effects);
  next = updateFacing(next, previousPosition);

  const afterGoal = next.memory.lastKnown?.sourceId ?? null;
  if (next.behavior !== beforeState || afterGoal !== beforeGoal) {
    effects.push({
      type: "decision",
      enemyId: next.id,
      atGameMinute: Math.round((context.gameMinutes ?? 0) * 10) / 10,
      from: beforeState,
      to: next.behavior,
      goal: next.memory.lastKnown
        ? { sourceId: next.memory.lastKnown.sourceId, kind: next.memory.lastKnown.kind, coordinate: next.memory.lastKnown.coordinate }
        : null,
      position: [...next.position],
    });
  }

  return { enemy: next, effects };
}

// A stationary or just-spawned enemy keeps its last facing; a moving one
// faces the direction it just walked. Recomputed every tick rather than
// stored as an order, since movement (not intent) is what vision keys off.
function updateFacing(enemy, previousPosition) {
  if (!Array.isArray(previousPosition) || !Array.isArray(enemy.position)) return enemy;
  if (distanceInMeters(previousPosition, enemy.position) < 0.1) return enemy;
  return { ...enemy, facingDegrees: facingBearingDegrees(previousPosition, enemy.position) };
}

// --- Sensing --------------------------------------------------------------

function sense(enemy, context, effects) {
  const { gameMinutes } = context;
  let next = enemy;

  // Contact/attack: adjacency within the vision cone (and not occluded), no
  // reaction delay. Cone-gated on purpose — an enemy can only strike what it
  // can actually see, so slipping close behind or beside a patrol is safe.
  // Getting caught in the cone at point-blank range is the lethal moment.
  const contact = nearestTargetInCone(next, context, {
    facingDegrees: next.facingDegrees ?? 0,
    coneDegrees: context.visionConeDegrees ?? VISION_CONE_DEGREES,
    rangeMeters: context.contactRangeMeters ?? ENEMY_CONTACT_METERS,
    occluders: context.occluders ?? null,
  });
  if (contact) {
    const canRepeat = !next.memory.lastContactAtGameMinute
      || gameMinutes - next.memory.lastContactAtGameMinute >= CONTACT_REPEAT_MINUTES;
    if (canRepeat) {
      effects.push({ type: "contact", targetId: contact.id, targetKind: contact.kind, coordinate: [...contact.coordinate] });
      next.memory.lastContactAtGameMinute = gameMinutes;
    }
    return rememberTarget(next, contact, "contact", gameMinutes, { immediate: true });
  }

  // Sight: cone-gated + occlusion-gated, refreshes last-known continuously
  // while visible. A guard on station sees a wider, longer arc (enemyVisionFor).
  const vision = enemyVisionFor(next, context.visionConeDegrees ?? VISION_CONE_DEGREES, context.sightMeters ?? VISION_RANGE_METERS);
  const seen = nearestTargetInCone(next, context, {
    facingDegrees: next.facingDegrees ?? 0,
    coneDegrees: vision.coneDegrees,
    rangeMeters: vision.sightMeters,
    occluders: context.occluders ?? null,
  });
  if (seen) {
    return rememberTarget(next, seen, "sight", gameMinutes, { immediate: next.behavior === ENEMY_STATES.pursue });
  }

  // Hearing: position with lag, never overrides pursuit's better information.
  if (next.behavior !== ENEMY_STATES.pursue) {
    const heard = loudestNoiseAt(next, context);
    if (heard && gameMinutes >= (next.memory.noiseRefreshAtGameMinute || 0)) {
      next.memory.noiseRefreshAtGameMinute = gameMinutes + NOISE_REFRESH_MINUTES;
      return rememberNoise(next, heard, gameMinutes);
    }
  }

  return next;
}

function loudestNoiseAt(enemy, context) {
  let best = null;
  for (const noise of context.noises || []) {
    if (!Array.isArray(noise.coordinate)) continue;
    const distance = distanceInMeters(enemy.position, noise.coordinate);
    if (distance > noise.radiusMeters) continue;
    const loudness = noise.radiusMeters - distance;
    if (!best || loudness > best.loudness) best = { ...noise, distance, loudness };
  }
  return best;
}

function rememberTarget(enemy, target, kind, gameMinutes, { immediate = false } = {}) {
  const next = { ...enemy, memory: { ...enemy.memory } };
  const coordinate = Array.isArray(target) ? target : target.coordinate;
  next.memory.lastKnown = { coordinate: [...coordinate], kind, atGameMinute: gameMinutes, sourceId: Array.isArray(target) ? null : target.id ?? null };
  if (immediate || enemy.behavior === ENEMY_STATES.pursue) {
    next.behavior = ENEMY_STATES.pursue;
    next.memory.pendingState = null;
    next.memory.reactAtGameMinute = null;
  } else {
    scheduleState(next, ENEMY_STATES.pursue, gameMinutes);
  }
  return next;
}

function rememberNoise(enemy, noise, gameMinutes) {
  const next = { ...enemy, memory: { ...enemy.memory } };
  next.memory.lastKnown = { coordinate: [...noise.coordinate], kind: "noise", atGameMinute: gameMinutes, sourceId: noise.id ?? null };
  if (enemy.behavior === ENEMY_STATES.alerted) return next;
  scheduleState(next, ENEMY_STATES.alerted, gameMinutes);
  return next;
}

function scheduleState(enemy, state, gameMinutes) {
  const pendingPriority = STATE_PRIORITY[enemy.memory.pendingState] ?? -1;
  const currentPriority = STATE_PRIORITY[enemy.behavior] ?? 0;
  if (STATE_PRIORITY[state] <= currentPriority) return;
  if (STATE_PRIORITY[state] <= pendingPriority) return;
  enemy.memory.pendingState = state;
  enemy.memory.reactAtGameMinute = gameMinutes + REACTION_DELAY_MINUTES;
}

function applyPendingState(enemy, context) {
  const memory = enemy.memory;
  if (!memory.pendingState || context.gameMinutes < (memory.reactAtGameMinute ?? Infinity)) return enemy;
  return { ...enemy, behavior: memory.pendingState, memory: { ...memory, pendingState: null, reactAtGameMinute: null } };
}

// --- Acting -----------------------------------------------------------

function act(enemy, context, effects) {
  const { gameMinutes } = context;
  switch (enemy.behavior) {
    case ENEMY_STATES.pinned: {
      if (gameMinutes < (enemy.blockedUntilGameMinute ?? 0)) {
        return { ...enemy, lastAdvancedAtGameMinute: gameMinutes, awaitingPath: false };
      }
      return startWandering(enemy, context, effects);
    }
    case ENEMY_STATES.alerted:
    case ENEMY_STATES.pursue: {
      if (beyondLeash(enemy)) return startWandering(enemy, context, effects);
      const destination = enemy.memory.lastKnown?.coordinate;
      if (!destination) return startWandering(enemy, context, effects);
      let next = ensurePathToward(enemy, destination, context);
      next = advanceAlongPath(next, gameMinutes);
      if (!next.awaitingPath) return next;
      if (next.behavior === ENEMY_STATES.pursue
        && nearestTargetInCone(next, context, { facingDegrees: next.facingDegrees ?? 0, coneDegrees: context.visionConeDegrees ?? VISION_CONE_DEGREES, rangeMeters: context.sightMeters ?? VISION_RANGE_METERS, occluders: context.occluders ?? null })) {
        return { ...next, lastAdvancedAtGameMinute: gameMinutes, awaitingPath: false };
      }
      return startSearching(next, context, effects);
    }
    case ENEMY_STATES.search: {
      if (gameMinutes >= (enemy.memory.searchUntilGameMinute ?? 0)) return startWandering(enemy, context, effects);
      const advanced = advanceAlongPath(enemy, gameMinutes);
      if (!advanced.awaitingPath) return advanced;
      return assignNextWanderCorridor(advanced, context);
    }
    case ENEMY_STATES.wander:
    default: {
      if (enemy.enemyType === ENEMY_TYPES.guard) return actGuard(enemy, context);
      if (enemy.enemyType === ENEMY_TYPES.patroller) return actPatroller(enemy, context);
      if (enemy.enemyType === ENEMY_TYPES.raider) return actRaider(enemy, context, effects);
      const advanced = advanceAlongPath(enemy, gameMinutes);
      if (!advanced.awaitingPath) return advanced;
      return assignNextWanderCorridor(advanced, context);
    }
  }
}

// A shut door within reach and roughly on the way to `goal` that the raider
// should chip. The assigned egress door is preferred (it's the raider's target
// even before any field object exists — see the assault-only test); otherwise
// any intact door field object that sits between the raider and its goal, so a
// raider that has breached its own door and is flooding deeper chews through an
// interior door blocking the path instead of stalling outside it.
function doorToBreachToward(enemy, goal, context, assignedDoor) {
  if (assignedDoor && Array.isArray(assignedDoor.doorCoordinate)
    && distanceInMeters(enemy.position, assignedDoor.doorCoordinate) <= RAIDER_DOOR_REACH_METERS) {
    return { objectId: assignedDoor.doorObjectId, coordinate: assignedDoor.doorCoordinate };
  }
  if (!Array.isArray(goal)) return null;
  const toGoal = [goal[0] - enemy.position[0], goal[1] - enemy.position[1]];
  let best = null;
  for (const object of Object.values(context.fieldObjects || {})) {
    if (object.kind !== "door" || object.breached || !Array.isArray(object.coordinate)) continue;
    const distance = distanceInMeters(enemy.position, object.coordinate);
    if (distance > RAIDER_DOOR_REACH_METERS) continue;
    // Only a door ahead of the raider (toward the goal), not one it just cleared.
    const toDoor = [object.coordinate[0] - enemy.position[0], object.coordinate[1] - enemy.position[1]];
    if (toDoor[0] * toGoal[0] + toDoor[1] * toGoal[1] <= 0) continue;
    if (!best || distance < best.distance) best = { objectId: object.id, coordinate: object.coordinate, distance };
  }
  return best;
}

// --- Raider idle: assault the assigned egress door, then flood inside ---------
// The raider heads for its door; a shut door within reach on the way (its egress
// door, then any interior door blocking the flood) gets swung at instead of a
// move — the door soaks the hit (emitting attack-object) and breaches at 0 HP
// (resolveTacticalTurn applies the damage). Once past, the goal is the interior.
function actRaider(enemy, context, effects) {
  const { gameMinutes } = context;
  const assault = enemy.assault;
  if (!assault) { // no assignment (shouldn't happen) — drift like a plain enemy
    const advanced = advanceAlongPath(enemy, gameMinutes);
    return advanced.awaitingPath ? assignNextWanderCorridor(advanced, context) : advanced;
  }

  const door = assault.doorObjectId ? context.fieldObjects?.[assault.doorObjectId] : null;
  const doorShut = Boolean(door) && !door.breached;
  const goal = doorShut ? assault.doorCoordinate : (assault.interiorCoordinate ?? assault.doorCoordinate);

  const breach = doorToBreachToward(enemy, goal, context, doorShut ? assault : null);
  if (breach) {
    // In reach of a shut door on the path: swing at it, hold position, face it.
    effects.push({ type: "attack-object", objectId: breach.objectId, damage: RAIDER_DOOR_DAMAGE, coordinate: [...enemy.position], enemyId: enemy.id });
    return {
      ...enemy,
      facingDegrees: facingBearingDegrees(enemy.position, breach.coordinate),
      lastAdvancedAtGameMinute: gameMinutes,
      awaitingPath: false,
    };
  }

  if (!Array.isArray(goal)) {
    const advanced = advanceAlongPath(enemy, gameMinutes);
    return advanced.awaitingPath ? assignNextWanderCorridor(advanced, context) : advanced;
  }
  // Re-path whenever the goal switches (door just breached) so the committed
  // route doesn't keep aiming at the door after it's open.
  const goalKey = doorShut ? "door" : "interior";
  let next = enemy;
  if (enemy.memory?.assaultGoalKey !== goalKey) {
    next = { ...enemy, memory: { ...(enemy.memory || {}), assaultGoalKey: goalKey, pathTargetCoordinate: null, lastRepathAtGameMinute: null } };
  }
  next = ensurePathToward(next, goal, context);
  const advanced = advanceAlongPath(next, gameMinutes);
  return { ...advanced, awaitingPath: false };
}

// --- Guard idle: hold an intersection, rotate facing every 3–5 turns --------

function guardRotateInterval(enemy, rotations) {
  const span = GUARD_ROTATE_MAX_TURNS - GUARD_ROTATE_MIN_TURNS + 1;
  return GUARD_ROTATE_MIN_TURNS + (hashInt(`${enemy.id}:${rotations}`) % span);
}

function actGuard(enemy, context) {
  const gameMinutes = context.gameMinutes;
  const anchor = enemy.anchorCoordinate;

  // Off station (walked away during a chase): route back to the post first.
  if (Array.isArray(anchor) && distanceInMeters(enemy.position, anchor) > GUARD_ON_POST_METERS && typeof context.routeTo === "function") {
    const route = context.routeTo(enemy.position, anchor);
    if (route?.reachable && Array.isArray(route.coordinates) && route.coordinates.length >= 2) {
      const path = route.coordinates.map((coordinate) => [...coordinate]);
      const walked = advanceAlongPathByMeters({ position: [...enemy.position], path, pathIndex: 0 }, enemy.speedMetersPerGameMinute ?? 0, gameMinutes);
      return { ...enemy, position: walked.position, path, pathIndex: walked.pathIndex, target: [...path.at(-1)], lastAdvancedAtGameMinute: gameMinutes, awaitingPath: walked.awaitingPath };
    }
  }

  // On station: stand at the intersection and periodically re-aim down a
  // different street of it. No x/y movement, so updateFacing() won't clobber
  // the facing we set here.
  const facings = Array.isArray(enemy.guardFacings) && enemy.guardFacings.length ? enemy.guardFacings : [enemy.facingDegrees ?? 0];
  let facingIndex = enemy.guardFacingIndex ?? 0;
  let facingDegrees = facings[facingIndex] ?? enemy.facingDegrees ?? 0;
  let rotations = enemy.guardRotations ?? 0;
  let nextTurnAt = enemy.guardNextTurnAtGameMinute ?? (gameMinutes + guardRotateInterval(enemy, rotations));
  if (gameMinutes >= nextTurnAt && facings.length > 1) {
    facingIndex = (facingIndex + 1) % facings.length;
    facingDegrees = facings[facingIndex];
    rotations += 1;
    nextTurnAt = gameMinutes + guardRotateInterval(enemy, rotations);
  }
  const post = Array.isArray(anchor) ? [...anchor] : [...enemy.position];
  return {
    ...enemy,
    position: post,
    facingDegrees,
    guardFacingIndex: facingIndex,
    guardRotations: rotations,
    guardNextTurnAtGameMinute: nextTurnAt,
    path: [post],
    pathIndex: 0,
    target: post,
    lastAdvancedAtGameMinute: gameMinutes,
    awaitingPath: true,
  };
}

// --- Patroller idle: pace one street, pausing a full turn to about-face ------

// Reorient onto the assigned patrol corridor, walking toward whichever end is
// farther so the patroller sweeps the whole street. Used to resume patrolling
// after a chase pulls it off its route.
function resumePatrol(enemy, context) {
  const coords = enemy.patrolCoordinates;
  if (!Array.isArray(coords) || coords.length < 2) {
    const advanced = advanceAlongPath(enemy, context.gameMinutes);
    return advanced.awaitingPath ? assignNextWanderCorridor(advanced, context) : advanced;
  }
  const toStart = distanceInMeters(enemy.position, coords[0]);
  const toEnd = distanceInMeters(enemy.position, coords.at(-1));
  const oriented = (toStart <= toEnd ? coords : [...coords].reverse()).map((coordinate) => [...coordinate]);
  const path = distanceInMeters(enemy.position, oriented[0]) > 0.5 ? [[...enemy.position], ...oriented] : oriented;
  return { ...enemy, path, pathIndex: 0, position: [...path[0]], target: [...path.at(-1)], patrolTurning: false, awaitingPath: false, lastAdvancedAtGameMinute: context.gameMinutes };
}

function actPatroller(enemy, context) {
  const gameMinutes = context.gameMinutes;

  // Mid about-face: one whole turn spent rotating 180° in place (no x/y move),
  // then reversed to walk back down the street.
  if (enemy.patrolTurning) {
    const coords = Array.isArray(enemy.patrolCoordinates) && enemy.patrolCoordinates.length >= 2 ? enemy.patrolCoordinates : enemy.path;
    // We're sitting at one end; orient the pure corridor to walk toward the other.
    const atStart = distanceInMeters(enemy.position, coords[0]) <= distanceInMeters(enemy.position, coords.at(-1));
    const oriented = (atStart ? coords : [...coords].reverse()).map((coordinate) => [...coordinate]);
    return {
      ...enemy,
      patrolTurning: false,
      path: oriented,
      pathIndex: 0,
      position: [...oriented[0]],
      target: [...oriented.at(-1)],
      facingDegrees: ((enemy.facingDegrees ?? 0) + 180) % 360,
      lastAdvancedAtGameMinute: gameMinutes,
      awaitingPath: false,
    };
  }

  const advanced = advanceAlongPath(enemy, gameMinutes);
  if (!advanced.awaitingPath) return advanced;
  // Reached the end of the street this turn: spend the next full turn about-facing.
  return { ...advanced, patrolTurning: true, awaitingPath: true };
}

function ensurePathToward(enemy, destination, context) {
  const gameMinutes = context.gameMinutes;
  const lastRepath = enemy.memory.lastRepathAtGameMinute;
  const hasCommittedPath = Array.isArray(enemy.memory.pathTargetCoordinate);
  const dueForRepath = lastRepath == null || gameMinutes - lastRepath >= REPATH_INTERVAL_GAME_MINUTES;
  if (hasCommittedPath && !dueForRepath) return enemy;
  const route = context.routeTo(enemy.position, destination);
  if (!route?.reachable || !Array.isArray(route.coordinates) || route.coordinates.length < 2) return enemy;
  return {
    ...enemy,
    position: [...route.coordinates[0]],
    path: route.coordinates.map((coordinate) => [...coordinate]),
    pathIndex: 0,
    target: [...route.coordinates.at(-1)],
    awaitingPath: false,
    memory: { ...enemy.memory, pathTargetCoordinate: [...destination], lastRepathAtGameMinute: gameMinutes },
  };
}

function startSearching(enemy, context, effects) {
  // A raider never mills around looking for a lost target — it has one purpose,
  // assaulting the base, so it drops straight back into its assault instead of
  // corridor-drifting away from the objective (the "breach a door then wander
  // off" bug). Everyone else searches where the trail went cold.
  if (enemy.enemyType === ENEMY_TYPES.raider) return startWandering(enemy, context, effects);
  return assignNextWanderCorridor({
    ...enemy,
    behavior: ENEMY_STATES.search,
    memory: { ...enemy.memory, lastKnown: null, pathTargetCoordinate: null, lastRepathAtGameMinute: null, searchUntilGameMinute: context.gameMinutes + SEARCH_DWELL_MINUTES },
  }, context);
}

function startWandering(enemy, context, effects) {
  const calmed = {
    ...enemy,
    behavior: ENEMY_STATES.wander,
    memory: { ...enemy.memory, lastKnown: null, pathTargetCoordinate: null, lastRepathAtGameMinute: null, searchUntilGameMinute: null },
  };
  // Typed enemies go back to their post/beat rather than drifting a corridor;
  // a raider resumes flooding toward its objective (chipping doors on the way).
  if (calmed.enemyType === ENEMY_TYPES.guard) return actGuard(calmed, context);
  if (calmed.enemyType === ENEMY_TYPES.patroller) return resumePatrol(calmed, context);
  if (calmed.enemyType === ENEMY_TYPES.raider) return actRaider(calmed, context, effects ?? []);
  return assignNextWanderCorridor(calmed, context);
}

// --- Movement primitives ---------------------------------------------------
//
// advanceAlongPathByMeters is the meters-only primitive: it never looks at
// gameMinutes, only a meters budget. advanceAlongPath (the continuous-world
// call shape) is a thin wrapper that turns a gameMinutes delta into a meters
// budget via the entity's speed. tactical-turn.mjs calls the meters primitive
// directly with each unit's/enemy's fixed per-turn movement budget, so a
// "turn" never needs to pretend it's a span of game-minutes.

export function advanceAlongPathByMeters(entity, remainingMetersIn, atGameMinute) {
  const path = Array.isArray(entity.path) && entity.path.length ? entity.path : [entity.position];
  let position = Array.isArray(entity.position) ? [...entity.position] : path[0];
  let pathIndex = Math.max(0, Math.min(entity.pathIndex || 0, path.length - 1));
  let remainingMeters = Math.max(0, remainingMetersIn);

  while (remainingMeters > 0 && pathIndex < path.length - 1) {
    const target = path[pathIndex + 1];
    const segmentMeters = distanceInMeters(position, target);
    if (segmentMeters <= remainingMeters || segmentMeters === 0) {
      position = [...target];
      pathIndex += 1;
      remainingMeters -= segmentMeters;
    } else {
      const ratio = remainingMeters / segmentMeters;
      position = [position[0] + (target[0] - position[0]) * ratio, position[1] + (target[1] - position[1]) * ratio];
      remainingMeters = 0;
    }
  }

  return {
    ...entity,
    position,
    path,
    pathIndex,
    target: path.at(-1),
    lastAdvancedAtGameMinute: atGameMinute,
    awaitingPath: pathIndex >= path.length - 1,
  };
}

export function advanceAlongPath(enemy, gameMinutes) {
  if (!Number.isFinite(gameMinutes)) throw new Error("Enemy movement requires a finite game minute.");
  const speed = enemy.speedMetersPerGameMinute;
  if (!Number.isFinite(speed) || speed < 0) throw new Error("Enemy speed must be a non-negative number.");
  const remainingMeters = Math.max(0, gameMinutes - (enemy.lastAdvancedAtGameMinute ?? gameMinutes)) * speed;
  return advanceAlongPathByMeters(enemy, remainingMeters, gameMinutes);
}

function beyondLeash(enemy) {
  const anchor = enemy.anchorCoordinate;
  if (!Array.isArray(anchor)) return false;
  return distanceInMeters(enemy.position, anchor) > LEASH_RADIUS_METERS;
}

function outsideTerritory(enemy) {
  const anchor = enemy.anchorCoordinate;
  if (!Array.isArray(anchor)) return false;
  return distanceInMeters(enemy.position, anchor) > TERRITORY_RADIUS_METERS;
}

function nearestHomeNode(coordinate, graph) {
  let best = null;
  for (const corridor of graph.corridors.values()) {
    for (const [nodeId, endpoint] of [[corridor.startNodeId, corridor.coordinates[0]], [corridor.endNodeId, corridor.coordinates.at(-1)]]) {
      const distance = distanceInMeters(coordinate, endpoint);
      if (!best || distance < best.distance) best = { nodeId, coordinate: endpoint, corridorId: corridor.id, distance };
    }
  }
  return best;
}

function routeHome(enemy, context) {
  const anchor = enemy.anchorCoordinate;
  const graph = context.graph;
  if (!Array.isArray(anchor) || typeof context.routeTo !== "function" || !graph?.corridors) return null;
  const home = nearestHomeNode(anchor, graph);
  if (!home) return null;
  const route = context.routeTo(enemy.position, home.coordinate);
  if (!route?.reachable || !Array.isArray(route.coordinates) || route.coordinates.length < 2) return null;
  return {
    ...enemy,
    position: [...route.coordinates[0]],
    path: route.coordinates.map((coordinate) => [...coordinate]),
    pathIndex: 0,
    target: [...route.coordinates.at(-1)],
    lastAdvancedAtGameMinute: context.gameMinutes,
    awaitingPath: false,
    pathCorridorId: home.corridorId,
    targetNodeId: home.nodeId,
  };
}

function assignNextWanderCorridor(enemy, context) {
  const graph = context.graph;
  const gameMinutes = context.gameMinutes;
  if (!graph?.corridors) return { ...enemy, lastAdvancedAtGameMinute: gameMinutes, awaitingPath: true };

  const strayed = outsideTerritory(enemy);
  if (strayed) {
    const homeward = routeHome(enemy, context);
    if (homeward) return homeward;
  }

  const currentNodeId = currentEnemyGraphNodeId(enemy, graph);
  if (!currentNodeId) return { ...enemy, lastAdvancedAtGameMinute: gameMinutes, awaitingPath: true };
  const anchor = enemy.anchorCoordinate;
  const farEndpoint = (corridor) => (corridor.startNodeId === currentNodeId ? corridor.coordinates.at(-1) : corridor.coordinates[0]);
  const visits = enemy.pathVisits || new Map();
  const candidates = [...graph.corridors.values()]
    .filter((corridor) => corridor.startNodeId === currentNodeId || corridor.endNodeId === currentNodeId)
    .sort((a, b) => {
      if (strayed && Array.isArray(anchor)) {
        const homeDelta = distanceInMeters(farEndpoint(a), anchor) - distanceInMeters(farEndpoint(b), anchor);
        if (Math.abs(homeDelta) > 1) return homeDelta;
      }
      const visitDelta = (visits.get(a.id) ?? -Infinity) - (visits.get(b.id) ?? -Infinity);
      if (visitDelta) return visitDelta;
      return a.id.localeCompare(b.id);
    });
  const corridor = candidates[0];
  if (!corridor) return { ...enemy, lastAdvancedAtGameMinute: gameMinutes, awaitingPath: true };
  const pathVisits = new Map(enemy.pathVisits || []);
  pathVisits.set(corridor.id, (pathVisits.get(corridor.id) || 0) + 1);
  const forward = corridor.startNodeId === currentNodeId;
  const path = forward ? corridor.coordinates.map((coordinate) => [...coordinate]) : [...corridor.coordinates].reverse().map((coordinate) => [...coordinate]);
  const connectedPath = distanceInMeters(enemy.position, path[0]) > 0.5 ? [[...enemy.position], ...path] : path;
  return {
    ...enemy,
    position: [...connectedPath[0]],
    path: connectedPath,
    pathIndex: 0,
    target: [...connectedPath.at(-1)],
    lastAdvancedAtGameMinute: gameMinutes,
    awaitingPath: false,
    pathCorridorId: corridor.id,
    targetNodeId: forward ? corridor.endNodeId : corridor.startNodeId,
    pathVisits,
  };
}

function currentEnemyGraphNodeId(enemy, graph) {
  const current = enemy.targetNodeId;
  if (current) {
    const corridor = graph.corridors.get(enemy.pathCorridorId);
    const coordinate = corridor?.startNodeId === current ? corridor.coordinates[0] : corridor?.endNodeId === current ? corridor.coordinates.at(-1) : null;
    if (coordinate && distanceInMeters(enemy.position, coordinate) <= 12) return current;
  }
  return nearestGraphNodeId(enemy.position, graph);
}

function nearestGraphNodeId(coordinate, graph) {
  let nearest = null;
  for (const corridor of graph.corridors.values()) {
    for (const [nodeId, endpoint] of [[corridor.startNodeId, corridor.coordinates[0]], [corridor.endNodeId, corridor.coordinates.at(-1)]]) {
      const distance = distanceInMeters(coordinate, endpoint);
      if (!nearest || distance < nearest.distance) nearest = { nodeId, distance };
    }
  }
  return nearest?.distance <= 12 ? nearest.nodeId : null;
}

export function pinEnemyAtRoadblock(enemy, gameMinutes, confusedMinutes) {
  return {
    ...enemy,
    behavior: ENEMY_STATES.pinned,
    blockedAtCoordinate: [...enemy.position],
    blockedUntilGameMinute: gameMinutes + confusedMinutes,
    path: [enemy.position],
    pathIndex: 0,
    target: enemy.position,
    lastAdvancedAtGameMinute: gameMinutes,
    awaitingPath: false,
  };
}

export function enemyStateMarker(enemy) {
  const state = normalizeEnemyState(enemy?.behavior);
  if (state === ENEMY_STATES.pursue) return "‼";
  if (state === ENEMY_STATES.alerted) return "!";
  if (state === ENEMY_STATES.search || state === ENEMY_STATES.pinned) return "?";
  return "";
}
